import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Card, Checkbox, Tabs, Tag, Tooltip } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText, toBoolean } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  CollaborationCircumscriptionRow,
  CollaborationLanguageRow,
  ListResponse,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

type Scope = "speaker" | "local_coordinator";

/**
 * The endpoint names differ per role but nothing else does, so the paths are
 * the only thing the scope decides.
 */
const PATHS = {
  speaker: {
    languages: "/collaboration/speaker_languages_grid",
    language: "/collaboration/speaker_language_collaboration",
    circumscriptions: "/collaboration/speaker_circumscriptions_grid",
    circumscription: "/collaboration/speaker_circumscription_collaboration",
  },
  local_coordinator: {
    languages: "/collaboration/local_coord_languages_grid",
    language: "/collaboration/local_coord_language_collaboration",
    circumscriptions: "/collaboration/local_coord_circumscriptions_grid",
    circumscription: "/collaboration/local_coord_circumscription_collaboration",
  },
} as const;

/**
 * Two sidebar entries, one screen:
 *
 * - "speaker_collaborations"    → /collaboration/speaker_collaborations
 * - "local_coord_collaborations" → /collaboration/local_coord_collaborations
 *
 * Which language areas and which circumscriptions the role works with. A
 * speaker reaches the talks of a language they collaborate in; a coordinator
 * finds the speakers of a circumscription they collaborate with.
 *
 * The own language and the own circumscription are always part of it and cannot
 * be switched off — the server declines, and the rows are shown as fixed.
 *
 * Worth knowing when reading the server: a Collaboration row means *included*
 * for a language and *excluded* for a circumscription. The endpoints hide that
 * inversion — PUT always means collaborate, DELETE always means stop — so this
 * screen treats both alike.
 */
export function CollaborationsPage({ scope }: { scope: Scope }) {
  const { t } = useTranslation();

  const title =
    scope === "speaker"
      ? t("navigation.speaker_collaborations")
      : t("navigation.local_coord_collaborations");

  return (
    <Card title={title}>
      <Tabs
        items={[
          {
            key: "languages",
            label: t("collaboration.add_language_collaboration"),
            children: (
              <LanguageTab
                scope={scope}
                exportAs={`${title} · ${t("activerecord.models.language")}`}
              />
            ),
          },
          {
            key: "circumscriptions",
            label: t("collaboration.add_circumscription_collaboration"),
            children: (
              <CircumscriptionTab
                scope={scope}
                exportAs={`${title} · ${t("activerecord.models.circumscription")}`}
              />
            ),
          },
        ]}
      />
    </Card>
  );
}

/** Shared plumbing: both tabs toggle one row at a time against two verbs. */
function useToggle(queryKey: unknown[], path: string) {
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  return useMutation({
    mutationFn: ({ id, collaborate }: { id: number; collaborate: boolean }) =>
      apiFetch<NotificationsResponse>(`${path}/${id}`, {
        method: collaborate ? "PUT" : "DELETE",
      }),
    onSuccess: (data) => {
      // Answers 200 with a notice even when it declined — "your own
      // circumscription is included by default" is not an error.
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: showError,
  });
}

function LanguageTab({ scope, exportAs }: { scope: Scope; exportAs: string }) {
  const { t } = useTranslation();
  const queryKey = useMemo(() => ["collaboration", scope, "languages"], [scope]);

  const languages = useQuery({
    queryKey,
    queryFn: () => apiFetch<ListResponse<CollaborationLanguageRow>>(PATHS[scope].languages),
  });

  const toggle = useToggle(queryKey, PATHS[scope].language);

  const columns = useMemo<ExportableColumn<CollaborationLanguageRow>[]>(
    () => [
      {
        title: t("collaboration.add_language_collaboration"),
        dataIndex: "language_collaboration",
        width: 220,
        // 0/1 from the server; the tick is only how the cell draws it.
        exportValue: (row) => toBoolean(row.language_collaboration),
        render: (value, row) => (
          <Checkbox
            checked={toBoolean(value)}
            disabled={row.own || toggle.isPending}
            onChange={(event) =>
              toggle.mutate({ id: row.id, collaborate: event.target.checked })
            }
          />
        ),
      },
      {
        title: t("activerecord.attributes.language.language_code"),
        dataIndex: "language_code",
        width: 100,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
      {
        title: t("activerecord.attributes.language.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        render: (value: string | null, row) =>
          row.own ? (
            <Tooltip title={t("collaboration.own_circumscription_language_included")}>
              <span>
                {value} <Tag>{t("activerecord.models.circumscription")}</Tag>
              </span>
            </Tooltip>
          ) : (
            value
          ),
      },
    ],
    [t, toggle],
  );

  return (
    <DataTable<CollaborationLanguageRow>
      columns={columns}
      rows={languages.data?.data}
      exportAs={exportAs}
      loading={languages.isPending || toggle.isPending}
      error={languages.error}
    />
  );
}

function CircumscriptionTab({ scope, exportAs }: { scope: Scope; exportAs: string }) {
  const { t } = useTranslation();
  const queryKey = useMemo(() => ["collaboration", scope, "circumscriptions"], [scope]);

  const circumscriptions = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<CollaborationCircumscriptionRow>>(PATHS[scope].circumscriptions),
  });

  const toggle = useToggle(queryKey, PATHS[scope].circumscription);

  const columns = useMemo<ExportableColumn<CollaborationCircumscriptionRow>[]>(
    () => [
      {
        title: t("collaboration.add_circumscription_collaboration"),
        dataIndex: "circumscription_collaboration",
        width: 220,
        exportValue: (row) => toBoolean(row.circumscription_collaboration),
        render: (value, row) => (
          <Checkbox
            checked={toBoolean(value)}
            disabled={row.own || toggle.isPending}
            onChange={(event) =>
              toggle.mutate({ id: row.id, collaborate: event.target.checked })
            }
          />
        ),
      },
      {
        title: t("activerecord.attributes.circumscription.circumscription_code"),
        dataIndex: "circumscription_code",
        width: 100,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.attributes.circumscription.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        render: (value: string | null, row) =>
          row.own ? (
            <Tooltip title={t("collaboration.own_circumscription_included")}>
              <span>
                {value} <Tag>{t("activerecord.models.circumscription")}</Tag>
              </span>
            </Tooltip>
          ) : (
            value
          ),
      },
    ],
    [t, toggle],
  );

  return (
    <DataTable<CollaborationCircumscriptionRow>
      columns={columns}
      rows={circumscriptions.data?.data}
      exportAs={exportAs}
      loading={circumscriptions.isPending || toggle.isPending}
      error={circumscriptions.error}
    />
  );
}
